import type { HTMLAttributes, ReactNode } from 'react'
import type { GradientPreset } from '../gradient-text/presets.ts'
import { GradientText } from '../gradient-text/GradientText.tsx'
import styles from './DisplayHeading.module.css'

export type DisplayHeadingSize = '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | '8xl'
export type DisplayHeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div'

export interface DisplayHeadingProps extends HTMLAttributes<HTMLElement> {
  /** Rendered element. Pick for document outline, not for size. */
  as?: DisplayHeadingElement
  size?: DisplayHeadingSize
  /**
   * `strong` is the poster cut (narrow + heavy) for section heads; `soft` is
   * the slightly wider, lighter cut that stays readable at card-heading sizes.
   */
  cut?: 'strong' | 'soft'
  /** Presentational uppercase; the DOM text is left untouched. */
  uppercase?: boolean
  /** Fills the whole heading with a gradient, via GradientText. */
  gradient?: GradientPreset
  from?: string
  to?: string
  via?: string
  children?: ReactNode
}

const sizeClass: Record<DisplayHeadingSize, string> = {
  '3xl': styles.s3xl,
  '4xl': styles.s4xl,
  '5xl': styles.s5xl,
  '6xl': styles.s6xl,
  '7xl': styles.s7xl,
  '8xl': styles.s8xl,
}

export function DisplayHeading({
  as: Tag = 'h2',
  size = '6xl',
  cut = 'strong',
  uppercase = true,
  gradient,
  from,
  to,
  via,
  className,
  children,
  ...props
}: DisplayHeadingProps) {
  const classes = [
    styles.heading,
    sizeClass[size],
    cut === 'soft' ? styles.soft : styles.strong,
    uppercase ? styles.uppercase : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const hasGradient = Boolean(gradient ?? from ?? to ?? via)

  return (
    <Tag className={classes} {...props}>
      {hasGradient ? (
        <GradientText gradient={gradient} from={from} to={to} via={via} angle={115}>
          {children}
        </GradientText>
      ) : (
        children
      )}
    </Tag>
  )
}
